import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkOrdersQuery } from '../state/workorders.query';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { WorkOrdersRepository } from '../workorders.repository';
import { WorkorderDetailActionsComponent } from '../workorder-detail-actions/workorder-detail-actions.component';
import { EmployeesRepository } from 'src/employees/employees.repository';
import { EmployeeVisitsRepository } from 'src/employee-visits/employeeVisits.repository';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmployeeVisitsQuery } from 'src/employee-visits/state/employeeVisits.query';
import { EmployeesQuery } from 'src/employees/state/employees.query';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { WorkorderBadgeComponent } from '../workorder-badge/workorder-badge.component';
import { EmployeeVisitsService } from 'src/employee-visits/employee-visits.service';
import { EmployeeVisitDetails } from './employee-visit-details';
import { TranslateModule } from '@ngx-translate/core';
import { WorkOrderTranslateService } from 'src/app/shared/services/translate-workorder.service';
import { LocalizedDatePipe } from '../../app/shared/localizedDatePipe';
import { AttachedFilesDetailsComponent } from './attached-files-details/attached-files-details.component';
import { AttachedFileDto } from 'src/api';
import { EmployeeVisitsDetailsComponent } from './employee-visits-details/employee-visits-details.component';

@Component({
  selector: 'app-workorder-detail',
  standalone: true,
  templateUrl: './workorder-detail.component.html',
  styleUrls: ['./workorder-detail.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    WorkorderDetailActionsComponent,
    MatTabsModule,
    MatIconModule,
    WorkorderBadgeComponent,
    TranslateModule,
    LocalizedDatePipe,
    AttachedFilesDetailsComponent,
    EmployeeVisitsDetailsComponent,
  ],
})
export class WorkorderDetailComponent implements OnInit {
  workOrder$ = this.workOrdersQuery.selectEntity(this.workOrderId);
  employeeVisitsDetail$: Observable<EmployeeVisitDetails[]>;
  employeeTeam$: Observable<string>;

  employeeVisitsIsVisible$: Observable<boolean>;
  attachedFilesIsVisible$: Observable<boolean>;

  attachedFiles$: Observable<AttachedFileDto[]>;
  previouslyAttachedFiles$: Observable<AttachedFileDto[]>;

  currentTab: BehaviorSubject<string> = new BehaviorSubject<string>('Description');
  currentTab$: Observable<string> = this.currentTab.asObservable();

  get workOrderId() {
    return this.activatedRoute.snapshot.params['id'];
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private workOrdersQuery: WorkOrdersQuery,
    private workorderRepository: WorkOrdersRepository,
    private employeesRepository: EmployeesRepository,
    private employeeVisitsRepository: EmployeeVisitsRepository,
    private employeeVisitsService: EmployeeVisitsService,
    private employeesQuery: EmployeesQuery,
    private employeeVisitsQuery: EmployeeVisitsQuery,
    private workorderTranslateService: WorkOrderTranslateService
  ) {
    this.employeeVisitsDetail$ = combineLatest([
      this.employeesQuery.selectAll(),
      this.employeeVisitsQuery.selectAll({
        filterBy: (employeeVisit) => employeeVisit.workOrderId == this.workOrderId,
      }),
    ]).pipe(
      takeUntilDestroyed(),
      map(([employees, employeeVisits]) => {
        const visitsByStartDate = employeeVisits.reduce(function (groups: any, employeeVisit) {
          const startDate = employeeVisit.startDate!.split('T')[0];
          if (!groups[startDate]) {
            groups[startDate] = [];
          }

          groups[startDate].push(employeeVisit);
          return groups;
        }, {});

        const visitsByDay = Object.keys(visitsByStartDate)
          .map((day) => {
            const visitsByDay = this.employeeVisitsService.getVisitsByDay(visitsByStartDate[day], employees);
            const visitsByEmployee = this.employeeVisitsService.getVisitsByEmployee(visitsByDay);

            return {
              day: new Date(day!),
              employeeNames: this.employeeVisitsService.getEmployeeDisplayNames([
                ...new Set(
                  visitsByEmployee.map((ev) => {
                    return ev.employeeAbbreviatedName;
                  })
                ),
              ]),
              visitsByEmployee: visitsByEmployee,
              totalHours: this.employeeVisitsService.getTotalHoursForDay(visitsByStartDate[day]),
              showDetail: false,
            } as EmployeeVisitDetails;
          })
          .sort((a, b) => new Date(b.day!).getTime() - new Date(a.day!).getTime());

        return visitsByDay;
      })
    );

    this.employeeTeam$ = combineLatest([
      this.employeesQuery.selectAll(),
      this.employeeVisitsQuery.selectAll({
        filterBy: (employeeVisit) => employeeVisit.workOrderId == this.workOrderId,
      }),
      this.workOrder$,
    ]).pipe(
      takeUntilDestroyed(),
      map(([employees, employeeVisits, workOrder]) => {
        let formatedTeamName = '';
        if (workOrder) {
          const specialistsNames =
            workOrder && workOrder.specialistIds
              ? workOrder!.specialistIds?.map((id) => {
                  const employee = employees.find((e) => e.id == id);
                  return employee ? employee.name : '';
                })
              : [];

          const employeesVisitNames = employeeVisits
            ? employeeVisits.map((ev) => {
                const employee = employees.find((e) => e.id == ev.employeeId);
                return employee ? employee.name : '';
              })
            : [];

          const translateAndChar = workorderTranslateService.translateAndCharacter();
          const employeeTeam = [...new Set([...specialistsNames, ...employeesVisitNames])].join(', ');

          formatedTeamName = this.employeeVisitsService.setCharAt(
            employeeTeam,
            employeeTeam.lastIndexOf(','),
            translateAndChar
          );
        }

        return formatedTeamName;
      })
    );

    this.employeeVisitsIsVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        const currentTab = this.currentTab.value;
        const isVisible =
          workOrder?.workOrderState === 'Working' ||
          workOrder?.workOrderState === 'Validated' ||
          workOrder?.workOrderState === 'Finished';

        if (currentTab === 'Visits' && !isVisible) {
          this.changeCurrentTab('Description');
        }

        return isVisible;
      })
    );

    this.attachedFilesIsVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        const currentTab = this.currentTab.value;
        const isVisible = workOrder?.workOrderState === 'Validated' || workOrder?.workOrderState === 'Finished';

        if (currentTab === 'AttachedFiles' && !isVisible) {
          this.changeCurrentTab('Description');
        }

        return isVisible;
      })
    );

    this.attachedFiles$ = this.workOrder$.pipe(
      map((workOrder) => {
        let attachedFiles: AttachedFileDto[] = [];
        if (workOrder && workOrder.attachedFiles) {
          workOrder.attachedFiles.map((a) => {
            if (!a.isPreviouslyAttachedFile) {
              attachedFiles.push(a);
            }
          });
        }

        return attachedFiles.sort((a, b) => new Date(b.modifiedDate!).getTime() - new Date(a.modifiedDate!).getTime());
      })
    );

    this.previouslyAttachedFiles$ = this.workOrder$.pipe(
      map((workOrder) => {
        let attachedFiles: AttachedFileDto[] = [];
        if (workOrder && workOrder.attachedFiles) {
          workOrder.attachedFiles.map((a) => {
            if (a.isPreviouslyAttachedFile) {
              attachedFiles.push(a);
            }
          });
        }

        return attachedFiles.sort((a, b) => new Date(b.modifiedDate!).getTime() - new Date(a.modifiedDate!).getTime());
      })
    );
  }

  ngOnInit(): void {
    if (!this.workOrdersQuery.hasEntity(this.workOrderId)) {
      this.workorderRepository.getWorkOrder(this.workOrderId);
    } else {
      this.workorderRepository.loadWorkOrderAttachedFiles(this.workOrderId);
    }

    this.employeesRepository.loadEmployees();
    this.employeeVisitsRepository.loadWorkOrderEmployeeVisits(this.workOrderId);
  }

  changeCurrentTab(currentTab: string) {
    this.currentTab.next(currentTab);
  }

  translateWorkOrderState(workOrderState: string) {
    return this.workorderTranslateService.translateWorkOrderState(workOrderState);
  }
}
